import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { maybe } from "@saleor/misc";
import {
  SearchBrands,
  SearchBrandsVariables
} from "@saleor/searches/types/SearchBrands";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import moment from "moment";

import { IFilterElement } from "../../../../components/Filter";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleValueQueryParam,
  getSingleEnumValueQueryParam
} from "../../../../utils/filters";
import { removeBase64Name } from "../../../utils/misc";
import {
  StoreOrderBoolean,
  StoreOrdersBiFilterKeys,
  StoreOrdersBiListFilterOpts
} from "../../components/StoreOrdersBiListPage/filters";
import {
  InitialStoreOrdersBiFilterData_brands_edges_node,
  InitialStoreOrdersBiFilterData_malls_edges_node
} from "../../types/InitialStoreOrdersBiFilterData";
import {
  storeOrdersBiListUrlFilters,
  StoreOrdersBiListUrlFiltersEnum,
  StoreOrdersBiListUrlFiltersWithMultipleValues,
  StoreOrdersBiUrlQueryParams
} from "../../urls";
export const SALESREPORTS_FILTERS_KEY = "StoreOrdersBiFilters";

export function getFilterOpts(
  params: storeOrdersBiListUrlFilters,
  brands: {
    initial: InitialStoreOrdersBiFilterData_brands_edges_node[];
    search: UseSearchResult<SearchBrands, SearchBrandsVariables>;
  },
  malls: {
    initial: InitialStoreOrdersBiFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): StoreOrdersBiListFilterOpts {
  return {
    brand: {
      active: !!params.brand,
      choices: maybe(
        () =>
          brands.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.brand
        ? maybe(
            () =>
              brands.initial.map(brand => ({
                label: brand.name,
                value: brand.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => brands.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: brands.search.result.loading,
      onFetchMore: brands.search.loadMore,
      onSearchChange: brands.search.search,
      value: maybe(() => dedupeFilter(params.brand), [])
    },

    completedAt: {
      active: maybe(
        () =>
          [params.orderCompleteDateFrom, params.orderCompleteDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderCompleteDateTo, ""),
        min: maybe(() => params.orderCompleteDateFrom, "")
      }
    },
    isPayoutEligible: {
      active: !!params.isPayoutEligible,
      value: maybe(() => params.isPayoutEligible, "")
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    orderDate: {
      active: maybe(
        () =>
          [params.orderDateFrom, params.orderDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderDateTo, ""),
        min: maybe(() => params.orderDateFrom, "")
      }
    },
    orderStatus: {
      active: !!params.orderStatus,
      value: maybe(() => dedupeFilter(params.orderStatus), [])
    },
    shippingMethodName: {
      active: !!params.deliveryType,
      value: maybe(() => dedupeFilter(params.deliveryType), [])
    }
  };
}

export function getFilterVariables(params: storeOrdersBiListUrlFilters) {
  const newDate = new Date();
  const endDay = newDate.getDate();
  const endMonth = newDate.getMonth() + 1;
  const endYear = newDate.getFullYear();

  const startDate = moment(newDate.setMonth(newDate.getMonth() - 3)).format(
    "YYYY-MM-DD"
  );
  const endDate = moment(`${endYear}-${endMonth}-${endDay} 00:00:00`)
    .add(1, "days")
    .format("YYYY-MM-DD HH:mm:ssZZ");

  return {
    brandId: removeBase64Name(
      "Brand:",
      params.brand !== undefined ? params.brand : []
    ),
    completedAt: getGteLteVariables({
      gte: params.orderCompleteDateFrom
        ? moment(`${params.orderCompleteDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : null,
      lte: params.orderCompleteDateTo
        ? moment(`${params.orderCompleteDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : null
    }),
    isPayoutEligible: params?.isPayoutEligible
      ? params.isPayoutEligible === "true"
      : null,
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? params.mall : []
    ),

    orderDate: getGteLteVariables({
      gte: params.orderDateFrom
        ? moment(`${params.orderDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : !params.orderDateFrom && params.orderCompleteDateFrom
        ? null
        : moment(`${startDate} 00:00:00`).format("YYYY-MM-DD HH:mm:ssZZ"),

      lte: params.orderDateTo
        ? moment(`${params.orderDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : !params.orderDateTo && params.orderCompleteDateTo
        ? null
        : endDate
    }),
    orderStatus: maybe(() => params.orderStatus, []),
    search: params.query,
    shippingMethodName: params.deliveryType
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<StoreOrdersBiFilterKeys>,
  params: storeOrdersBiListUrlFilters
): storeOrdersBiListUrlFilters {
  const { active, group, name, value } = filter;

  if (!!group) {
    const rest = params && params[group] ? params[group] : undefined;

    return {
      [group]: active
        ? {
            ...(rest === undefined ? {} : rest),
            [name]: value
          }
        : rest
    };
  }

  switch (name) {
    case StoreOrdersBiFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        StoreOrdersBiListUrlFiltersWithMultipleValues.mall
      );
    case StoreOrdersBiFilterKeys.brand:
      return getMultipleValueQueryParam(
        filter,
        StoreOrdersBiListUrlFiltersWithMultipleValues.brand
      );

    case StoreOrdersBiFilterKeys.orderDate:
      return getMinMaxQueryParam(
        filter,
        StoreOrdersBiListUrlFiltersEnum.orderDateFrom,
        StoreOrdersBiListUrlFiltersEnum.orderDateTo
      );
    case StoreOrdersBiFilterKeys.completedAt:
      return getMinMaxQueryParam(
        filter,
        StoreOrdersBiListUrlFiltersEnum.orderCompleteDateFrom,
        StoreOrdersBiListUrlFiltersEnum.orderCompleteDateTo
      );
    case StoreOrdersBiFilterKeys.isPayoutEligible:
      return getSingleEnumValueQueryParam(
        filter,
        StoreOrdersBiListUrlFiltersEnum.isPayoutEligible,
        StoreOrderBoolean
      );
    case StoreOrdersBiFilterKeys.orderStatus:
      return getMultipleValueQueryParam(
        filter,
        StoreOrdersBiListUrlFiltersWithMultipleValues.orderStatus
      );
    case StoreOrdersBiFilterKeys.shippingMethodName:
      return getMultipleValueQueryParam(
        filter,
        StoreOrdersBiListUrlFiltersWithMultipleValues.deliveryType
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<storeOrdersBiListUrlFilters>(SALESREPORTS_FILTERS_KEY);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  StoreOrdersBiUrlQueryParams,
  storeOrdersBiListUrlFilters
>({
  ...StoreOrdersBiListUrlFiltersEnum,
  ...StoreOrdersBiListUrlFiltersWithMultipleValues
});
